import { CloudinaryImage, CloudinaryVideo } from '@cloudinary/url-gen';

/**
 * Builds `src` + `srcSet` so the browser picks one correctly-sized image.
 *
 * The alternative, `@cloudinary/react`'s `responsive()` plugin, costs two
 * downloads per image: it cannot measure a container during SSR, so the
 * server-rendered `src` carries the full-width URL, the browser fetches it,
 * and only after hydration does the plugin measure the container and swap in a
 * smaller one. `srcSet` lets the browser make that choice before the first
 * byte, from markup that server-renders correctly.
 */

/**
 * Candidate widths, in *device* pixels, offered for card-sized imagery.
 *
 * The browser multiplies the CSS slot width from `sizes` by the device pixel
 * ratio and picks the smallest candidate that covers it, so these already
 * account for retina and must NOT be paired with `.delivery(dpr(2))`, which
 * would double them a second time and ship a 4x image.
 *
 * The ladder spans what the marketplace grid actually asks for: ~440 device px
 * on a 1x desktop up to ~1470 on a 2x tablet, where the grid drops to a single
 * wide column.
 */
export const CARD_IMAGE_WIDTHS = [400, 500, 600, 800, 1000, 1200, 1500];

/**
 * CSS width of a marketplace card image slot, measured across viewports: ~356px
 * on a phone (one column), ~734px on a tablet (one wide grid column), and
 * 389-450px on desktop, where `auto-fill` keeps adding columns rather than
 * widening them. The desktop figure is rounded up to leave a little headroom.
 */
export const CARD_IMAGE_SIZES =
  '(max-width: 36em) 92vw, (max-width: 62em) 95vw, 460px';

/**
 * Slot widths for the app's "My listings" grid, which sets its columns
 * explicitly rather than by `auto-fill`: 4 above 95em, then 3, 2 and 1.
 */
export const APP_CARD_IMAGE_SIZES =
  '(max-width: 36em) 92vw, (max-width: 62em) 46vw, (max-width: 95em) 31vw, 23vw';

/**
 * The listing-detail media column: full width below `large`, half the row above
 * it, and never wider than the 600px the carousel caps a slide at.
 */
export const DETAIL_MEDIA_SIZES = '(max-width: 62em) 92vw, 600px';

/** Candidate widths for the 100px carousel thumbnail strip, up to 3x. */
export const CAROUSEL_THUMBNAIL_WIDTHS = [100, 200, 300];

/** CSS width of a carousel thumbnail (`thumbWidth` on the carousel). */
export const CAROUSEL_THUMBNAIL_SIZES = '100px';

/** Candidate widths for the 160x130 seller-logo box on listing detail, up to 3x. */
export const SELLER_LOGO_WIDTHS = [160, 320, 480];

/** CSS width of that seller-logo box. */
export const SELLER_LOGO_SIZES = '160px';

/**
 * Candidate widths for the PWA logo on the auth pages. Its column is
 * `minmax(400px, 20%)` less 40px padding either side, so ~320px on desktop,
 * and it stacks to a full-width row capped at `80vw` below `medium`.
 */
export const AUTH_LOGO_WIDTHS = [320, 480, 640, 960];

/** CSS width of that logo: 80vw once the auth layout stacks, else its column. */
export const AUTH_LOGO_SIZES = '(max-width: 48em) 80vw, 320px';

/** Candidate widths for the 80px seller-logo thumbnail, up to 3x. */
export const THUMBNAIL_IMAGE_WIDTHS = [80, 160, 240];

/** CSS width of the seller-logo thumbnail. */
export const THUMBNAIL_IMAGE_SIZES = '80px';

/**
 * Width used for the plain `src`, which only a client without `srcSet` support
 * would load. Mid-ladder, so such a client gets something reasonable either way.
 */
const FALLBACK_WIDTH = 800;

interface ResponsiveImageProps {
  src: string;
  srcSet: string;
}

/**
 * `buildAsset` is called once per candidate width because the width belongs in
 * the crop itself. Appending a second resize to an already-capped URL would
 * upscale the capped output rather than deliver more detail.
 */
export const buildResponsiveImage = (
  buildAsset: (width: number) => CloudinaryImage | CloudinaryVideo,
  widths: number[] = CARD_IMAGE_WIDTHS
): ResponsiveImageProps => ({
  src: buildAsset(FALLBACK_WIDTH).toURL(),
  srcSet: widths
    .map((width) => `${buildAsset(width).toURL()} ${width}w`)
    .join(', '),
});
